import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Radio, Clock, ShoppingBag, CreditCard, Plus, Trash2, Printer, Search, Shield, Eye } from 'lucide-react';
import CashCalculator from '../components/CashCalculator';

const catStyles = {
  fundador: 'bg-purple-100 text-purple-800',
  normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800',
  eventos: 'bg-blue-100 text-blue-800',
};
const paymentMethods = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cortesia', label: 'Cortesia' },
  { value: 'cupon', label: 'Cupon' },
];

function getDuration(entryTime) {
  if (!entryTime) return '0m';
  const diff = Date.now() - new Date(entryTime).getTime();
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

export default function BraceletPage() {
  const { API, user: currentUser } = useAuth();
  const [braceletCode, setBraceletCode] = useState('');
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderForm, setOrderForm] = useState({ product_id: '', quantity: 1 });
  const [paymentMethod, setPaymentMethod] = useState('efectivo');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/settings`)
    ]).then(([p, s]) => {
      setProducts(p.data.filter(pr => pr.active));
      setSettings(s.data);
    }).catch(() => {});
  }, [API]);

  const searchBracelet = async (code) => {
    const searchCode = code || braceletCode;
    if (!searchCode.trim()) { toast.error('Ingresa el codigo de pulsera'); return; }
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await axios.get(`${API}/visits/bracelet/${searchCode.trim()}`);
      setVisit(data);
    } catch {
      setVisit(null);
      setNotFound(true);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') searchBracelet();
  };

  const refreshVisit = async () => {
    if (!visit) return;
    try {
      const { data } = await axios.get(`${API}/visits/${visit.id}`);
      setVisit(data);
    } catch { setVisit(null); }
  };

  const handleAddOrder = async () => {
    if (!orderForm.product_id || orderForm.quantity < 1) { toast.error('Selecciona producto y cantidad'); return; }
    try {
      const { data } = await axios.post(`${API}/visits/${visit.id}/orders`, orderForm);
      setVisit(data);
      setShowAddOrder(false);
      setOrderForm({ product_id: '', quantity: 1 });
      toast.success('Producto agregado');
      setTimeout(() => { const el = document.querySelector('[data-testid="bracelet-input"]'); if (el) el.focus(); }, 300);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCheckout = async () => {
    try {
      const { data } = await axios.post(`${API}/visits/${visit.id}/checkout`, { payment_method: paymentMethod, notes: '' });
      toast.success('Cobro realizado');
      setShowCheckout(false);
      setVisit(null);
      setBraceletCode('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const canCheckout = currentUser?.role === 'admin' || currentUser?.role === 'cajero';
  const isMesero = currentUser?.role === 'mesero';
  const roleLabel = { admin: 'Administrador', cajero: 'Cajero', mesero: 'Mesero' }[currentUser?.role] || '';
  const discountPct = visit && settings ? (settings.category_discounts?.[visit.client_category] || 0) : 0;
  const subtotal = visit?.subtotal || 0;
  const discountAmt = Math.round(subtotal * discountPct / 100 * 100) / 100;
  const finalTotal = Math.round((subtotal - discountAmt) * 100) / 100;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bracelet-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Consulta por Pulsera</h1>
          <p className="text-stone-500 text-sm mt-1">Escanea o ingresa el codigo RFID de la pulsera</p>
        </div>
        <div data-testid="role-badge" className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
          isMesero ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-green-50 text-green-800 border border-green-200'
        }`}>
          {isMesero ? <Eye className="w-4 h-4" strokeWidth={1.5} /> : <Shield className="w-4 h-4" strokeWidth={1.5} />}
          {isMesero ? 'Modo Mesero: Agregar productos' : `Modo ${roleLabel}: Acceso completo`}
        </div>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
        <CardContent className="p-6">
          <div className="flex gap-3 max-w-xl">
            <div className="relative flex-1">
              <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-600" strokeWidth={1.5} />
              <Input
                data-testid="bracelet-input"
                value={braceletCode}
                onChange={e => setBraceletCode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Codigo de pulsera RFID..."
                className="pl-11 rounded-xl border-stone-300 h-12 text-lg focus:ring-orange-600/20 focus:border-orange-600"
                autoFocus
              />
            </div>
            <Button data-testid="bracelet-search-btn" onClick={() => searchBracelet()} disabled={loading}
              className="bg-orange-700 hover:bg-orange-800 text-white rounded-xl h-12 px-6 font-medium">
              <Search className="w-4 h-4 mr-2" /> Buscar
            </Button>
          </div>
          <p className="text-xs text-stone-400 mt-2">El lector RFID escribira automaticamente el codigo. Presiona Enter para buscar.</p>
        </CardContent>
      </Card>

      {loading && <p className="text-stone-400 text-center py-8">Buscando...</p>}

      {notFound && !loading && (
        <Card className="bg-red-50 border border-red-200 rounded-2xl">
          <CardContent className="p-6 text-center">
            <p className="text-red-700 font-medium">No se encontro visita activa con esta pulsera</p>
            <p className="text-red-500 text-sm mt-1">Verifica el codigo o registra una entrada con esta pulsera</p>
          </CardContent>
        </Card>
      )}

      {visit && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-outfit text-xl font-semibold text-stone-900">{visit.client_name}</h2>
                  <Badge className={`${catStyles[visit.client_category] || catStyles.normal} border-0 rounded-full text-xs`}>{visit.client_category}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-xs text-stone-500">Entrada</p>
                    <p className="text-sm font-medium text-stone-900">{new Date(visit.entry_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-xs text-stone-500">Duracion</p>
                    <p className="text-sm font-medium text-stone-900">{getDuration(visit.entry_time)}</p>
                  </div>
                  {visit.locker_number && (
                    <div className="bg-stone-50 rounded-xl p-3">
                      <p className="text-xs text-stone-500">Locker</p>
                      <p className="text-sm font-medium text-stone-900">#{visit.locker_number}</p>
                    </div>
                  )}
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-xs text-stone-500">Mesero</p>
                    <p className="text-sm font-medium text-stone-900">{visit.waiter_name}</p>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Pulsera</p>
                  <p className="font-mono text-lg font-bold text-orange-700 mt-1">{visit.bracelet_code}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-stone-500">Subtotal</span><span className="text-stone-900">${subtotal.toFixed(2)}</span></div>
                  {discountPct > 0 && <div className="flex justify-between text-sm"><span className="text-orange-700">Descuento ({discountPct}%)</span><span className="text-orange-700">-${discountAmt.toFixed(2)}</span></div>}
                  <div className="flex justify-between text-xl font-bold border-t border-stone-200 pt-2 mt-2">
                    <span className="font-outfit text-stone-900">Total</span>
                    <span className="font-outfit text-orange-700">${finalTotal.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button data-testid="bracelet-add-order" onClick={() => setShowAddOrder(true)} className="flex-1 bg-orange-700 hover:bg-orange-800 text-white rounded-full text-sm">
                    <Plus className="w-4 h-4 mr-1" /> Agregar Producto
                  </Button>
                  {canCheckout && (
                    <Button data-testid="bracelet-checkout" onClick={() => setShowCheckout(true)} className="flex-1 bg-green-700 hover:bg-green-800 text-white rounded-full text-sm">
                      <CreditCard className="w-4 h-4 mr-1" /> Cobrar
                    </Button>
                  )}
                </div>
                {isMesero && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3 text-center" data-testid="mesero-restriction-msg">
                    Solo cajero o administrador puede cobrar esta cuenta
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
              <CardContent className="p-6">
                <h3 className="font-outfit font-medium text-stone-900 mb-4">Consumos ({(visit.orders || []).length})</h3>
                <div className="space-y-2">
                  {(visit.orders || []).map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900">{o.product_name}</p>
                        <p className="text-xs text-stone-500">{o.quantity} x ${o.unit_price.toFixed(2)} {o.is_entry_fee ? '(Cuota de entrada)' : ''}</p>
                      </div>
                      <span className="text-sm font-semibold text-stone-900 ml-4">${o.total.toFixed(2)}</span>
                    </div>
                  ))}
                  {(visit.orders || []).length === 0 && <p className="text-sm text-stone-400 text-center py-6">Sin consumos registrados</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Agregar Producto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Producto</Label>
              <Select value={orderForm.product_id} onValueChange={v => setOrderForm({ ...orderForm, product_id: v })}>
                <SelectTrigger data-testid="bracelet-order-product" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - ${p.price.toFixed(2)} {p.stock != null ? `(${p.stock})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cantidad</Label><Input data-testid="bracelet-order-qty" type="number" min="1" value={orderForm.quantity} onChange={e => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOrder(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="bracelet-order-submit" onClick={handleAddOrder} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Cobrar Visita</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-center py-2 space-y-2">
              <p className="text-stone-500 text-sm">Subtotal: ${subtotal.toFixed(2)}</p>
              {discountPct > 0 && <p className="text-orange-700 text-sm">Descuento {discountPct}%: -${discountAmt.toFixed(2)}</p>}
              <p className="font-outfit text-4xl font-bold text-orange-700">${finalTotal.toFixed(2)}</p>
            </div>
            <div>
              <Label>Metodo de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="bracelet-payment" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{paymentMethods.map(pm => <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {paymentMethod === 'efectivo' && <CashCalculator total={finalTotal} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="bracelet-checkout-confirm" onClick={handleCheckout} className="bg-green-700 hover:bg-green-800 text-white rounded-full">Confirmar Cobro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
